import React, { useState, useEffect } from 'react';
import { Film, MapPin, Calendar, Image, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { movieApi, mediaApi, cinemasApi } from '../../services/api';

const AdminDashboard = () => {
  const [movie, setMovie] = useState(null);
  const [cinemas, setCinemas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [movieRes, cinemasRes] = await Promise.all([
          movieApi.get(),
          cinemasApi.getAll()
        ]);
        setMovie(movieRes.data);
        setCinemas(cinemasRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const totalSessions = cinemas.reduce((acc, c) => acc + (c.showtimes?.length || 0), 0);

  const stats = [
    { label: 'Película Activa', value: movie?.title || '-', icon: Film, color: 'bg-blue-500' },
    { label: 'Cines Configurados', value: cinemas.length, icon: MapPin, color: 'bg-green-500' },
    { label: 'Sesiones Totales', value: totalSessions, icon: Calendar, color: 'bg-purple-500' },
    { label: 'Imágenes Subidas', value: '5', icon: Image, color: 'bg-orange-500' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500">Bienvenido al panel de administración</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => (
          <Card key={index}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Info */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Movie Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Film className="w-5 h-5 text-[#e84c8a]" />
              Información de la Película
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <img 
                src="https://m.media-amazon.com/images/M/MV5BNTg5NGI2NDUtMGJhNC00NjQzLTk1ZjAtNDU3MjE5MWM5NzA5XkEyXkFqcGc@._V1_.jpg"
                alt={movie?.title}
                className="w-24 h-36 object-cover rounded-lg"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1536440136628-849c177e76a1?w=200&q=80';
                }}
              />
              <div>
                <h3 className="text-lg font-semibold">{movie?.title}: {movie?.subtitle}</h3>
                <p className="text-sm text-gray-500">{movie?.tagline}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {movie?.genres?.map((genre) => (
                    <span key={genre} className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {genre}
                    </span>
                  ))}
                </div>
                <p className="text-sm text-gray-600 mt-2">{movie?.duration} • {movie?.rating}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Cinemas */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#e84c8a]" />
              Cines Recientes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {cinemas.slice(0, 4).map((cinema) => (
                <div key={cinema.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="font-medium text-gray-900">{cinema.name}</p>
                    <p className="text-sm text-gray-500">{cinema.address}</p>
                  </div>
                  <span className="text-sm text-[#e84c8a]">{cinema.showtimes?.length || 0} sesiones</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
